import React, { useState } from 'react';
import { FileText, AlertCircle, ImageIcon, FileIcon, FolderArchive } from 'lucide-react';
import useDocumentBlobUrl from '../hooks/useDocumentBlobUrl';
import { useLanguage } from '../i18n';
import logger from '../utils/logger';

const isImage = (att) => !!att?.is_image || (typeof att?.mime_type === 'string' && att.mime_type.startsWith('image/'));
const isPdf = (att) => {
    const mt = (att?.mime_type || '').toLowerCase();
    if (mt === 'application/pdf') return true;
    const fn = (att?.filename || '').toLowerCase();
    return fn.endsWith('.pdf');
};
// Can we show a visual thumbnail? (Images and PDFs — both supported by the BE /thumbnail endpoint.)
const canRenderThumb = (att) => isImage(att) || isPdf(att);

const humanSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Renders a single attachment for a chat message.
 * - Images + PDFs: inline thumbnail via /api/documents/<id>/thumbnail
 * - Other files: clickable file chip (opens DocumentPreviewModal via onClick)
 * - Upload failures: red warning chip
 *
 * `onClick(attachment)` is called when the user clicks a thumbnail or a chip —
 * the parent (Message.jsx) opens DocumentPreviewModal.
 */
const MessageAttachment = ({ attachment, onClick }) => {
    const thumbable = canRenderThumb(attachment) && !!attachment?.id;
    const { url: blobUrl, error: blobError } = useDocumentBlobUrl(
        thumbable ? attachment.id : null,
        thumbable,
        { path: 'thumbnail' },
    );
    const [imgBroken, setImgBroken] = useState(false);
    const urlError = !!blobError || imgBroken;

    if (blobError) {
        logger.error('ATTACH', `thumbnail fetch failed for ${attachment?.id}:`, blobError);
    }

    const handleClick = () => {
        if (!attachment?.id || !onClick) return;
        onClick(attachment);
    };

    if (attachment?.upload_error) {
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-xs text-red-700 dark:text-red-300">
                <AlertCircle size={14} />
                <span className="truncate max-w-[180px]">{attachment.filename || 'Attachment'}</span>
                <span className="text-[10px] opacity-70">upload failed</span>
            </div>
        );
    }

    // #513 — a ZIP the server unpacked into the File Manager: not a document
    // (no id), so no preview; the chip carries the unpacking summary.
    if (attachment?.archive_import && !attachment?.id) {
        return <ArchiveChip attachment={attachment} />;
    }

    if (thumbable) {
        return (
            <button
                type="button"
                onClick={handleClick}
                className="group relative overflow-hidden rounded-lg border border-pai-subtle bg-white/[0.06] hover:ring-2 hover:ring-white/40 transition-shadow"
                title={attachment.filename}
                style={{ width: 140, height: 140 }}
            >
                {blobUrl && !imgBroken ? (
                    <img
                        src={blobUrl}
                        alt={attachment.filename || 'attachment'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={() => {
                            logger.error('ATTACH', `img failed to render for ${attachment.id}`);
                            setImgBroken(true);
                        }}
                    />
                ) : urlError ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-pai-faint">
                        <ImageIcon size={20} />
                        <span className="mt-1 text-[10px]">preview unavailable</span>
                    </div>
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <div className="animate-pulse w-8 h-8 rounded-full bg-white/[0.12]" />
                    </div>
                )}
                {isPdf(attachment) && (
                    <div className="absolute top-1 right-1 bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                        PDF
                    </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 text-[10px] text-white truncate opacity-0 group-hover:opacity-100 transition-opacity">
                    {attachment.filename}
                </div>
            </button>
        );
    }

    // Non-image / non-pdf chip — clickable opens the preview modal
    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={!attachment?.id || !onClick}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-pai-subtle text-xs text-pai-secondary hover:bg-white/[0.09] disabled:hover:bg-white/[0.06] disabled:cursor-default min-w-0 max-w-full"
            title={`${attachment.filename}${attachment.file_size ? ` · ${humanSize(attachment.file_size)}` : ''}`}
        >
            <FileText size={14} className="text-pai-meta shrink-0" />
            <span className="truncate min-w-0 font-medium">{attachment.filename}</span>
            {attachment.file_size ? (
                <span className="text-[10px] text-pai-meta shrink-0">{humanSize(attachment.file_size)}</span>
            ) : null}
        </button>
    );
};

const ArchiveChip = ({ attachment }) => {
    const { t } = useLanguage();
    const count = attachment.archive_import?.saved_count ?? 0;
    return (
        <div
            data-testid="message-archive-chip"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-pai-subtle text-xs text-pai-secondary min-w-0 max-w-full"
            title={attachment.summary || attachment.filename}
        >
            <FolderArchive size={14} className="text-pai-meta shrink-0" />
            <span className="truncate min-w-0 font-medium">{attachment.filename}</span>
            <span className="text-[10px] text-pai-meta shrink-0">
                {t('attachArchiveFiles', '{n} files saved', { n: count })}
            </span>
        </div>
    );
};

export default MessageAttachment;
